'use client'

import { forwardRef, useEffect, useRef, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import Link from 'next/link'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { createClient } from '~/lib/supabase/client'
import { env } from '~/env'
import { Checkbox } from '~/components/ui/checkbox'
import { AppleButton, FacebookButton, GoogleButton } from '~/components/auth/SocialButtons'
import { isSsoProviderEnabled } from '~/lib/sso-providers'
import { resolvePostLoginSurface, setLoginType } from '~/services/user'
import { authActionsEnabled, isSignupRoute, termsConsentMetadata } from '~/lib/terms-acceptance'
import { captureReferralFromUrl, referralSignupMetadata } from '~/lib/referral-capture'
import { buildVerifyUrl } from './verify-url'
import { authEntryHref } from './auth-links'
import { authCallbackUrl, safeNextPath } from './return-path'

/** #869 — one spelling, because tickTerms() clears this and only this. */
const CONSENT_REQUIRED_ERROR =
  'Please accept the Terms of Service and Privacy Policy to continue'

export default function LoginForm() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const redirectParam = searchParams.get('redirect')
  // #1306 — where to go after sign-in, if the sending page said; same-origin
  // path only (see return-path.ts). Null means "the role surface", as before.
  const nextPath = safeNextPath(searchParams.get('next'))
  const fromLoginpage = searchParams.get('from') === 'loginpage'
  const isHostLogin = pathname?.includes('/hostlogin') || pathname?.includes('/hostsignup')
  // #1402 — a guest-checkout "create an account" prompt links here with
  // `?email=` already known; prefill it rather than making them retype it.
  // Only ever a starting value — the field stays freely editable.
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  // #512 — explicit consent, sign-up routes only. Starts FALSE and is never
  // seeded from anywhere: a pre-ticked box is not consent. /login and /signup
  // are the same component, so the route is what tells them apart — see
  // isSignupRoute. Someone who already has an account signs in at /login and
  // is never asked again.
  const isSignup = isSignupRoute(pathname)
  const [termsTicked, setTermsTicked] = useState(false)
  const actionsEnabled = authActionsEnabled({ pathname, termsTicked, loading })
  // #869 — a blocked click has to point somewhere. The error banner renders at
  // the TOP of the form while the social buttons sit at the bottom, so the
  // message alone is easy to miss; this ring + scroll puts the tick box back in
  // front of the user. Only one of the two form variants is mounted at a time,
  // so one ref covers both gates.
  const [consentNudge, setConsentNudge] = useState(false)
  const consentGateRef = useRef<HTMLDivElement | null>(null)

  const tickTerms = (next: boolean) => {
    setTermsTicked(next)
    if (next) {
      setConsentNudge(false)
      // Clear ONLY our own message. A tick answers the consent complaint and
      // nothing else, so wiping the banner wholesale would hide a live
      // "Invalid login credentials" or a failed OAuth attempt behind it.
      setError((current) => (current === CONSENT_REQUIRED_ERROR ? null : current))
    }
  }

  const supabase = createClient()

  // #1163 — `?ref=CODE` from an invite link is captured on /login and /signup
  // mount and kept in sessionStorage until the account is created. It had no
  // caller since the login page was rebuilt, so /me/invite promised PathCoins
  // and attached nobody. The code is handed to Supabase at the two creation
  // seams below (OTP `options.data`; OAuth via /auth/callback), where the API
  // reads it back as `referred_by_code` when it mints the account row.
  useEffect(() => {
    captureReferralFromUrl()
  }, [])

  /**
   * #512 — where OAuth comes back to.
   *
   * `terms=1` rides along only from a sign-up route with the box ticked;
   * /auth/callback stamps the acceptance onto the auth user once a session
   * exists, which is the first moment it can. signInWithOAuth has no
   * equivalent of signInWithOtp's `options.data`, so this is the seam.
   */
  const oauthCallbackUrl = () =>
    authCallbackUrl(window.location.origin, {
      // For host signups, redirect to facilitator signup to complete 13
      // questions; otherwise return the member to the page that sent them
      // here (#1306).
      next: isHostLogin ? '/facilitator/signup' : nextPath,
      terms: isSignup && termsTicked,
    })

  /**
   * #512 — the tick is a precondition, not a hint. The buttons are disabled
   * without it, but a keyboard path or a stale render must not be able to slip
   * past; on the sign-in routes this is always true and changes nothing.
   */
  const blockedByConsent = () => {
    if (actionsEnabled || loading) return false
    setError(CONSENT_REQUIRED_ERROR)
    setConsentNudge(true)
    consentGateRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    // Focus after the scroll is queued so the browser doesn't fight it.
    window.setTimeout(() => {
      document.getElementById('terms-consent')?.focus()
    }, 0)
    return true
  }

  const handleGoogleLogin = async () => {
    if (blockedByConsent()) return
    try {
      setLoading(true)
      setError(null)

      const callbackUrl = oauthCallbackUrl()

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: callbackUrl,
        },
      })

      if (error) {
        setError(error.message)
      }
    } catch (err) {
      setError('An unexpected error occurred')
      console.error('Login error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleFacebookLogin = async () => {
    if (blockedByConsent()) return
    try {
      setLoading(true)
      setError(null)

      const callbackUrl = oauthCallbackUrl()

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: callbackUrl,
        },
      })

      if (error) {
        setError(error.message)
      }
    } catch (err) {
      setError('An unexpected error occurred')
      console.error('Login error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleAppleLogin = async () => {
    if (blockedByConsent()) return
    try {
      setLoading(true)
      setError(null)

      const callbackUrl = oauthCallbackUrl()

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: callbackUrl,
        },
      })

      if (error) {
        setError(error.message)
      }
    } catch (err) {
      setError('An unexpected error occurred')
      console.error('Login error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleEmailPasswordLogin = async () => {
    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    if (!password.trim()) {
      setError('Please enter your password')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim(),
      })

      if (signInError) {
        setError(signInError.message || 'Invalid email or password')
        setLoading(false)
        return
      }

      if (data?.user) {
        // Check if user is a host (facilitator or admin) and redirect accordingly
        const { data: sessionData } = await supabase.auth.getSession()
        const token = sessionData.session?.access_token
        
        if (token) {
          // #354 — adopt any bookings made as a guest with this email.
          // Fire-and-forget: non-critical, re-runs on every login.
          void fetch(`${env.NEXT_PUBLIC_API_URL}/user/link-guest-bookings`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            keepalive: true,
          }).catch(() => {})

          try {
            const userRes = await fetch(`${env.NEXT_PUBLIC_API_URL}/user`, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            })
            
            // #1025 — archived/suspended/closed account: explain, sign out.
            if (userRes.status === 403) {
              router.push('/account-unavailable')
              return
            }

            if (userRes.ok) {
              const userData = await userRes.json()
              // Unified login: everyone (member / facilitator / admin) signs
              // in here and resumes their last active surface if still
              // entitled to it (docs/plans/unified-role-switching.md).
              const { surface, path } = resolvePostLoginSurface(userData)
              try {
                await setLoginType(surface)
              } catch (err) {
                console.error('Failed to persist login type:', err)
              }
              // #1306 — a return path from the sending page wins over the
              // role surface; the surface is still persisted above.
              router.push(nextPath ?? path)
              router.refresh()
              return
            }
          } catch (error) {
            console.error('Error fetching user data:', error)
          }
        }
        
        // Default redirect for members — /home, matching the root
        // dispatcher / logo / Home tab (#391); a return path still wins (#1306).
        router.push(nextPath ?? '/home')
        router.refresh()
      }
    } catch (err) {
      setError('An unexpected error occurred')
      console.error('Login error:', err)
      setLoading(false)
    }
  }

  const handleEmailContinue = async () => {
    if (blockedByConsent()) return
    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    try {
      setLoading(true)
      setError(null)

      // #512 — Supabase applies `data` only when it CREATES the user, so
      // this lands on exactly the sign-ups and never overwrites anything
      // for someone who already has an account. The API reads it back in
      // signupConsentSource() to record a tick rather than a notice.
      // #1163 — the captured invite code rides in the same fragment, under the
      // key `extractReferralCodeFromMetadata` reads. Sent from /login too: a
      // member who arrived at /login?ref= and creates an account here is a
      // referred sign-up, and Supabase ignores `data` for an existing user.
      const signupMetadata = {
        ...(isSignup && termsTicked ? termsConsentMetadata() : {}),
        ...referralSignupMetadata(),
      }
      // #1306 — only when a return path was given: the emailed link then lands
      // on /auth/callback?next=… (the same shape the Google button already
      // uses for host sign-ups) instead of the project's default redirect.
      // Host flows keep their own funnel via `redirect`, not `next`.
      const emailRedirectTo =
        nextPath && !isHostLogin
          ? authCallbackUrl(window.location.origin, { next: nextPath })
          : undefined
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: true,
          ...(emailRedirectTo ? { emailRedirectTo } : {}),
          ...(Object.keys(signupMetadata).length > 0 ? { data: signupMetadata } : {}),
        },
      })

      if (otpError) {
        setError(otpError.message)
        setLoading(false)
        return
      }

      // #798 — carry the page's intent through to /login/verify so the
      // "check your email" copy matches (signup vs sign-in). Was hardcoded
      // to intent=login, so /signup submissions got sign-in copy.
      router.push(buildVerifyUrl({ email, isSignup, isHostLogin, redirectParam, next: isHostLogin ? null : nextPath }))
    } catch (err) {
      setError('An unexpected error occurred')
      console.error('Login error:', err)
      setLoading(false)
    }
  }

  // Show email/password form when coming from loginpage
  if (fromLoginpage) {
    return (
      <div className="p-8">
        <div className="space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600">
              {error}
            </div>
          )}
          
          {/* Email Input */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-gray-700">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (error) setError(null)
              }}
              className="w-full"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && password.trim()) {
                  void handleEmailPasswordLogin()
                }
              }}
            />
          </div>

          {/* Password Input */}
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-gray-700">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  if (error) setError(null)
                }}
                className="w-full pr-10"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    void handleEmailPasswordLogin()
                  }
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Sign In Button.
              #512 — deliberately NOT gated by the consent tick. Password
              sign-in cannot create an account (signInWithPassword only
              authenticates an existing one), so everyone who reaches this
              button already accepted. Re-gating them would be friction that
              buys no consent. */}
          <Button
            onClick={handleEmailPasswordLogin}
            disabled={loading || !email.trim() || !password.trim()}
            className="w-full bg-[#DE866D] hover:bg-[#C97257] disabled:bg-[#DE866D] disabled:opacity-70 text-white h-11"
          >
            {loading ? 'Logging in...' : 'Log In'}
          </Button>

          <div className="text-center">
            <Link
              href="/forgot-password"
              className="text-sm text-[#DE866D] hover:underline"
            >
              Forgot password?
            </Link>
          </div>
        </div>

        {/* Divider */}
      <div className="flex justify-center my-6">
        <span className="text-sm text-gray-600">or log in with</span>
      </div>

      {/* The social buttons below CAN mint an account, unlike the password
          button above, so on a sign-up route they get the same tick gate as
          the main form (#512). */}
      <TermsConsentGate
        ref={consentGateRef}
        isSignup={isSignup}
        ticked={termsTicked}
        onTickedChange={tickTerms}
        highlight={consentNudge}
      />

        {/* Social Login Buttons */}
      <div className="space-y-3">
        {/* #554 — only providers enabled in Supabase render; the rest are
            env-gated (NEXT_PUBLIC_ENABLED_SSO_PROVIDERS) until V-2/V-3. */}
        {isSsoProviderEnabled('google') && (
          <GoogleButton handleGoogleLogin={handleGoogleLogin} loading={loading} disabled={!actionsEnabled} isSignup={isSignup} />
        )}
        {isSsoProviderEnabled('facebook') && (
          <FacebookButton handleFacebookLogin={handleFacebookLogin} loading={loading} disabled={!actionsEnabled} isSignup={isSignup} />
        )}
        {isSsoProviderEnabled('apple') && (
          <AppleButton handleAppleLogin={handleAppleLogin} loading={loading} disabled={!actionsEnabled} isSignup={isSignup} />
        )}
      </div>
      {/* #798 — this IS the password form; cross-link the other two entries:
          the magic-link sign-in and account creation. */}
      <div className="mt-6 text-center text-sm text-gray-600 space-y-1">
        <p>
          Prefer a one-tap email link?{' '}
          <a href={authEntryHref('magic-link', redirectParam, nextPath)} className="text-[#DE866D] hover:underline">Sign in with a magic link</a>
        </p>
        <p>
          New to Path Collective?{' '}
          <a href={authEntryHref('signup', redirectParam, nextPath)} className="text-[#DE866D] hover:underline">Create an account</a>
        </p>
      </div>
      </div>
    )
  }

  // #871 — the sign-up page keeps only what creating an account needs: no
  // "Forgot password?" (there is no password yet) and no "Prefer a
  // password?" (that is a returning user's door). The sign-in page keeps its
  // original order and both of those links. (#1419 moved the email field
  // ahead of Google on this page — see the isSignup block below — but #871's
  // omissions still apply.)
  const consentGate = (
    /* Consent gate. #512 — on the SIGN-UP routes this is an unticked box the
       user has to tick before anything below will fire (clickwrap); on the
       sign-in routes it stays the notice it was, because gating a sign-in
       behind a tick would re-gate everyone who already has an account.
       Sits above the actions either way: the recorded acceptance is only
       honest while the user was actually told first. */
    <TermsConsentGate
      ref={consentGateRef}
      isSignup={isSignup}
      ticked={termsTicked}
      onTickedChange={tickTerms}
      highlight={consentNudge}
    />
  )

  const emailField = (
    <div className="space-y-2">
      <Label htmlFor="email" className="text-sm font-medium text-gray-700">
        Email
      </Label>
      <Input
        id="email"
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value)
          if (error) setError(null)
        }}
        className="w-full"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            void handleEmailContinue()
          }
        }}
      />
    </div>
  )

  const continueButton = (
    /* #869 — the consent block is aria-disabled, not natively disabled, so
       the click still reaches handleEmailContinue() and gets an explanation.
       An empty email is a real disable: there is nothing to say yet. */
    <Button
      onClick={handleEmailContinue}
      disabled={loading || !email.trim()}
      aria-disabled={!actionsEnabled || undefined}
      className={`w-full bg-[#DE866D] disabled:bg-[#DE866D] disabled:opacity-70 text-white h-11 ${actionsEnabled ? 'hover:bg-[#C97257]' : 'opacity-70 cursor-not-allowed'}`}
    >
      {loading ? 'Processing...' : 'Continue'}
    </Button>
  )

  const socialButtons = (
    <div className="space-y-3">
      {/* #554 — only providers enabled in Supabase render; the rest are
          env-gated (NEXT_PUBLIC_ENABLED_SSO_PROVIDERS) until V-2/V-3. */}
      {isSsoProviderEnabled('google') && (
        <GoogleButton handleGoogleLogin={handleGoogleLogin} loading={loading} disabled={!actionsEnabled} isSignup={isSignup} />
      )}
      {isSsoProviderEnabled('facebook') && (
        <FacebookButton handleFacebookLogin={handleFacebookLogin} loading={loading} disabled={!actionsEnabled} isSignup={isSignup} />
      )}
      {isSsoProviderEnabled('apple') && (
        <AppleButton handleAppleLogin={handleAppleLogin} loading={loading} disabled={!actionsEnabled} isSignup={isSignup} />
      )}
    </div>
  )

  const errorBanner = error && (
    <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600">
      {error}
    </div>
  )

  if (isSignup) {
    return (
      <div className="space-y-4">
        {errorBanner}

        {/* #1419 — the tick now sits directly below the email field and
            immediately above the Continue button, so it is impossible to
            miss before the primary action. #869/#871's reasoning (gate sits
            right above the control it gates, no hint line needed) still
            holds — it now gates Continue instead of the Google button. */}
        {emailField}
        {consentGate}
        {continueButton}

        <div className="flex justify-center my-6">
          <span className="text-sm text-gray-600">or</span>
        </div>

        {socialButtons}

        {!isHostLogin && (
          <div className="mt-6 text-center text-sm text-gray-600">
            {/* #1300 — a returning member clicking "Sign in" expects the
                password form, not the magic-link screen; the password page
                itself cross-links the one-time email link. */}
            <p>
              Already a member?{' '}
              <a href={authEntryHref('password', redirectParam, nextPath)} className="text-[#DE866D] hover:underline">Sign in</a>
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {errorBanner}

      {emailField}
      {consentGate}
      {continueButton}

      <div className="text-center">
        <Link
          href="/forgot-password"
          className="text-sm text-[#DE866D] hover:underline"
        >
          Forgot password?
        </Link>
      </div>

      {/* Divider */}
      <div className="flex justify-center my-6">
        {/* #798 — the divider must match the page's intent, not assume signup */}
        <span className="text-sm text-gray-600">or log in with</span>
      </div>

      {socialButtons}

      {/* #741/#798 — the three auth entries, made legible from the magic-link
          pages: the password form, and the other intent (create vs sign in).
          Host flows keep their own funnel. */}
      {!isHostLogin && (
        <div className="mt-6 text-center text-sm text-gray-600 space-y-1">
          <p>
            Prefer a password?{' '}
            <a href={authEntryHref('password', redirectParam, nextPath)} className="text-[#DE866D] hover:underline">Sign in with your password</a>
          </p>
          <p>
            New to Path Collective?{' '}
            <a href={authEntryHref('signup', redirectParam, nextPath)} className="text-[#DE866D] hover:underline">Create an account</a>
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * The Terms of Service / Privacy Policy consent affordance — #511, #512.
 *
 * Two shapes, one decision: a tick the user must make on a sign-up route, a
 * plain notice on a sign-in route. The links are live in both, and the box
 * always starts unticked (the parent owns that state and never seeds it).
 */
const TermsConsentGate = forwardRef<HTMLDivElement, {
  isSignup: boolean
  ticked: boolean
  onTickedChange: (next: boolean) => void
  /** #869 — ring the box after a click we turned away, so the message lands. */
  highlight?: boolean
}>(function TermsConsentGate({ isSignup, ticked, onTickedChange, highlight }, ref) {
  const termsLink = (
    <a href="/terms" className="text-[#DE866D] hover:underline">
      Terms of Service
    </a>
  )
  const privacyLink = (
    <a href="/privacy" className="text-[#DE866D] hover:underline">
      Privacy Policy
    </a>
  )

  if (!isSignup) {
    return (
      <div ref={ref} className="text-center mb-4">
        <p className="text-xs text-gray-600">
          By continuing, you agree to our {termsLink} and {privacyLink}
        </p>
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className={`mb-4 flex items-start gap-2 rounded-md ${
        highlight ? 'ring-2 ring-[#DE866D] bg-[#FDF3EF] p-2 -m-2' : ''
      }`}
    >
      <Checkbox
        id="terms-consent"
        checked={ticked}
        onCheckedChange={(checked) => onTickedChange(checked === true)}
        className="mt-0.5"
      />
      <label
        htmlFor="terms-consent"
        className={`text-xs cursor-pointer ${highlight ? 'text-[#B4573B]' : 'text-gray-600'}`}
      >
        I agree to the {termsLink} and {privacyLink}
      </label>
    </div>
  )
})
